"use client"

import { useState, useEffect } from "react"
import { AlertTriangle, CheckCircle, Info, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface Announcement {
  id: string
  title: string
  message: string
  type: "info" | "warning" | "success" | "error"
  expiresAt?: string
}

export function AnnouncementBanner() {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    fetch("/api/settings/public")
      .then((res) => res.json())
      .then((data) => {
        if (data.announcement) {
          setAnnouncement(data.announcement)
          // Check if dismissed in localStorage
          const dismissedId = localStorage.getItem("announcement_dismissed")
          if (dismissedId === data.announcement.id) {
            setDismissed(true)
          }
        }
      })
      .catch(() => {})
  }, [])

  if (!announcement || dismissed) return null

  const getIcon = () => {
    switch (announcement.type) {
      case "info":
        return <Info className="h-5 w-5" />
      case "warning":
        return <AlertTriangle className="h-5 w-5" />
      case "success":
        return <CheckCircle className="h-5 w-5" />
      case "error":
        return <AlertTriangle className="h-5 w-5" />
    }
  }

  const getStyles = () => {
    switch (announcement.type) {
      case "info":
        return "bg-blue-500/10 border-blue-500/30 text-blue-500"
      case "warning":
        return "bg-yellow-500/10 border-yellow-500/30 text-yellow-500"
      case "success":
        return "bg-green-500/10 border-green-500/30 text-green-500"
      case "error":
        return "bg-red-500/10 border-red-500/30 text-red-500"
    }
  }

  const handleDismiss = () => {
    setDismissed(true)
    localStorage.setItem("announcement_dismissed", announcement.id)
  }

  return (
    <div className={cn("border-b border-border", getStyles())}>
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">{getIcon()}</div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm mb-1">{announcement.title}</h3>
            <p className="text-sm opacity-90">{announcement.message}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            onClick={handleDismiss}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
